// ---- Types ----

interface HslColor {
  h: number;
  s: number;
  l: number;
}

// ---- Range endpoints (from your palette) ----
// const RANGE_START: HslColor = { h: 232.5, s: 40, l: 96.08 }; // shade 50
// const RANGE_END: HslColor = { h: 231.11, s: 100, l: 10.59 }; // shade 950


const RANGE_START: HslColor = { h: 155, s: 100, l: 41 }; // shade 50
const RANGE_END: HslColor = { h: 342, s: 100, l: 65 }; // shade 950

// Average hue across your palette (they're all ~230-233, very tight)
const BASE_HUE = 231.5;

/**
 * cyrb53 — fast, high-quality 53-bit string hash with strong avalanche
 * behavior. Small input changes (e.g. "bob" -> "bot") produce
 * completely unrelated output. Deterministic: same input, same output.
 * Credit: public-domain implementation by bryc.
 */
function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;

  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }

  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/**
 * Deterministic pseudo-random float in [0, 1), derived from a seed.
 * Passing a different `salt` derives multiple *independent* random
 * values from the same username (hue wobble, saturation position,
 * lightness position, etc all vary separately).
 */
function seededRandom(str: string, salt = ''): number {
  const hash = cyrb53(str + salt);
  let t = hash + 0x6d2b79f5;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Applies a "softness" pass to a saturation/lightness pair.
 * - Pulls saturation down toward 0 (less vivid, more muted).
 * - Pulls lightness up slightly toward a mid-gray-ish tone.
 *
 * @param dimness - 0 = no change, 1 = fully muted/soft
 */
function applyDimness(s: number, l: number, dimness: number): { s: number; l: number } {
  const clampedDimness = clamp(dimness, 0, 1);
  const dimmedS = lerp(s, s * 0.35, clampedDimness);
  const dimmedL = lerp(l, lerp(l, 65, 0.6), clampedDimness);
  return { s: dimmedS, l: dimmedL };
}

/**
 * Correlated hue "temperature" tint based on lightness position.
 * Darker/more-saturated end of the range skews slightly toward violet
 * (+), lighter/desaturated end skews slightly toward blue (-). This
 * makes the range feel like one intentional gradient rather than a
 * random hue glued onto a random lightness.
 *
 * @param lightnessT - 0 = lightest end, 1 = darkest end
 * @param maxTintDegrees - max hue shift applied at the extremes
 */
function correlatedHueTint(lightnessT: number, maxTintDegrees = 6): number {
  // Map [0, 1] -> [-1, 1] so light end shifts negative, dark end positive
  const centered = lightnessT * 2 - 1;
  return centered * maxTintDegrees;
}

/**
 * Returns a unique, deterministic HSL color for a given username,
 * staying close to your brand's hue family.
 *
 * - Hue: BASE_HUE ± random wobble ± a small correlated tint based on
 *   how light/dark the color is (see correlatedHueTint).
 * - Saturation: independently hashed position along the S range.
 * - Lightness: independently hashed position along the L range.
 *   (Previously S and L moved together along one shared position —
 *   now they vary independently, roughly doubling the usable space.)
 *
 * @param options.hueVariance - random hue wobble in degrees (default 20)
 * @param options.hueTint - max correlated hue shift in degrees, tied to
 *   lightness (default 6). Set to 0 to disable.
 * @param options.satRange - [min, max] bounds (0-1) for saturation position
 * @param options.lightRange - [min, max] bounds (0-1) for lightness position
 * @param options.dimness - 0-1, how muted/soft the color should be
 */
export function getUserColor(
  username: string,
  options?: {
    hueVariance?: number;
    hueTint?: number;
    satRange?: [number, number];
    lightRange?: [number, number];
    dimness?: number;
  },
): string {
  const normalized = username.toLowerCase().trim();
  const hueVariance = options?.hueVariance ?? 20;
  const hueTintMax = options?.hueTint ?? 6;
  const [satMin, satMax] = options?.satRange ?? [0.2, 0.8];
  const [lightMin, lightMax] = options?.lightRange ?? [0.2, 0.8];
  const dimness = options?.dimness ?? 0;

  // Independent positions for saturation and lightness
  const sT = lerp(satMin, satMax, seededRandom(normalized, 'sat'));
  const lT = lerp(lightMin, lightMax, seededRandom(normalized, 'light'));

  const rawS = lerp(RANGE_START.s, RANGE_END.s, sT);
  const rawL = lerp(RANGE_START.l, RANGE_END.l, lT);

  // Hue = random wobble + tint correlated to lightness position
  const hueOffset = (seededRandom(normalized, 'hue') * 2 - 1) * hueVariance;
  const tint = correlatedHueTint(lT, hueTintMax);
  const hue = (BASE_HUE + hueOffset + tint + 360) % 360;

  const { s, l } = applyDimness(rawS, rawL, dimness);

  return `hsl(${hue.toFixed(2)}, ${s.toFixed(2)}%, ${l.toFixed(2)}%)`;
}

/**
 * Returns two HSL colors for a gradient, both within the same
 * controllable lightness range, so you can push the darker end
 * as dark as you want without dimness pulling it back toward gray.
 *
 * @param options.lightRange - [min, max] lightness position (0-1) for
 *   BOTH stops. e.g. [0.3, 0.75] keeps you away from near-white,
 *   but still lets the dark stop go genuinely dark.
 * @param options.spread - how far apart the two stops are along that
 *   range (0-1). Higher = more contrast between the two colors.
 * @param options.dimness - only affects saturation now, not lightness,
 *   so darkness is preserved.
 */
export function getUserGradientColors(
  username: string,
  options?: {
    lightRange?: [number, number];
    spread?: number;
    dimness?: number;
    hueVariance?: number;
    hueTint?: number;
  }
): { start: string; end: string; hue: number } {
  const normalized = username.toLowerCase().trim();
  const [lightMin, lightMax] = options?.lightRange ?? [0.15, 0.75];
  const spread = options?.spread ?? 0.35;
  const dimness = options?.dimness ?? 0;
  const hueVariance = options?.hueVariance ?? 20;
  const hueTintMax = options?.hueTint ?? 6;

  const hueOffset = (seededRandom(normalized, "hue") * 2 - 1) * hueVariance;

  // Pick a base position, then derive the second stop `spread` away from it
  const baseT = lerp(lightMin, lightMax - spread, seededRandom(normalized, "gradPos"));
  const startLT = baseT;
  const endLT = baseT + spread;

  const startSat = lerp(0.4, 0.9, seededRandom(normalized, "gradSat1"));
  const endSat = lerp(0.4, 0.9, seededRandom(normalized, "gradSat2"));

  const startHue = (BASE_HUE + hueOffset + correlatedHueTint(startLT, hueTintMax) + 360) % 360;
  const endHue = (BASE_HUE + hueOffset + correlatedHueTint(endLT, hueTintMax) + 360) % 360;

  const rawStartS = lerp(RANGE_START.s, RANGE_END.s, startSat);
  const rawStartL = lerp(RANGE_START.l, RANGE_END.l, startLT);
  const rawEndS = lerp(RANGE_START.s, RANGE_END.s, endSat);
  const rawEndL = lerp(RANGE_START.l, RANGE_END.l, endLT);

  // Only desaturate for dimness — leave lightness alone so the dark
  // stop stays genuinely dark instead of drifting toward mid-gray.
  const startS = lerp(rawStartS, rawStartS * 0.5, dimness);
  const endS = lerp(rawEndS, rawEndS * 0.5, dimness);

  return {
    start: `hsl(${startHue.toFixed(2)}, ${startS.toFixed(2)}%, ${rawStartL.toFixed(2)}%)`,
    end: `hsl(${endHue.toFixed(2)}, ${endS.toFixed(2)}%, ${rawEndL.toFixed(2)}%)`,
    hue: startHue,
  };
}
